import React, {
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import ReactRouterPropTypes from 'react-router-prop-types';
import { FormattedMessage } from 'react-intl';

import {
  Tooltip,
  IconButton,
} from '@folio/stripes/components';
import { withStripes } from '@folio/stripes/core';

import {
  JOB_TEMPLATE_FIELDS,
  JOB_TEMPLATE_SEARCHABLE_INDEXES,
  ICONS,
} from '../../../constants';
import { SearchAndFilter } from '../../common';
import { openItemDetail } from '../../../utils';
import css from './job-template-list.css';

const entity = 'job-template';

const jobTemplateNameField = `${entity}-${JOB_TEMPLATE_FIELDS.NAME}`;
const visibleColumns = [jobTemplateNameField];

const columnMapping = {
  [jobTemplateNameField]: <FormattedMessage id="ui-task-list.jobTemplate.field.name" />,
};

const resultsFormatter = {
  [jobTemplateNameField]: data => data[JOB_TEMPLATE_FIELDS.NAME]
};

const resultsPaneTitle = (
  <div className={css.titleWrap}>
    <span data-test-job-template-title>
      <FormattedMessage id="ui-task-list.title.list.jobTemplate" />
    </span>
    <Tooltip
      id="job-template-tooltip"
      text={<FormattedMessage id="ui-task-list.tooltip.jobTemplate.infoIcon" />}
    >
      {({ ref, ariaIds }) => (
        <IconButton
          data-test-job-template-info-icon
          ref={ref}
          aria-labelledby={ariaIds.text}
          icon={ICONS.INFO}
        />
      )}
    </Tooltip>
  </div>
);

const JobTemplateList = ({
  history,
  match,
  location,
  onNeedMoreData,
  resetData,
  jobTemplates,
  jobTemplatesCount,
  isLoading,
  children,
  stripes,
}) => {
  const hasEditCreatePermission = stripes.hasPerm('ui-task-list.job-template.create-edit');

  const openJobTemplateDetails = useCallback(
    (e, meta) => {
      openItemDetail({ history, path: match.path, location, id: meta.id });
    },
    [location.search],
  );

  return (
    <SearchAndFilter
      isPreRenderAllData
      columnMapping={columnMapping}
      contentData={jobTemplates}
      count={jobTemplatesCount}
      id="job-template-list"
      isLoading={isLoading}
      isShowAddNew={hasEditCreatePermission}
      resetData={resetData}
      resultsFormatter={resultsFormatter}
      resultsPaneTitle={resultsPaneTitle}
      searchableIndexes={JOB_TEMPLATE_SEARCHABLE_INDEXES}
      visibleColumns={visibleColumns}
      onNeedMoreData={onNeedMoreData}
      onRowClick={openJobTemplateDetails}
    >
      { children }
    </SearchAndFilter>
  );
};

JobTemplateList.propTypes = {
  history: ReactRouterPropTypes.history.isRequired,
  location: ReactRouterPropTypes.location.isRequired,
  match: ReactRouterPropTypes.match.isRequired,
  resetData: PropTypes.func.isRequired,
  stripes: PropTypes.shape({
    hasPerm: PropTypes.func.isRequired,
  }).isRequired,
  onNeedMoreData: PropTypes.func.isRequired,
  children: PropTypes.node,
  isLoading: PropTypes.bool,
  jobTemplates: PropTypes.arrayOf(PropTypes.object),
  jobTemplatesCount: PropTypes.number,
};

JobTemplateList.defaultProps = {
  jobTemplatesCount: 0,
  isLoading: false,
  jobTemplates: [],
};

export default withRouter(withStripes(JobTemplateList));
